import React from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { SEVERITY_COLOURS } from '../../fundamentals/colours/variants';
import { Span } from '../Texts/Texts';
import { FONT_SIZES } from '../../fundamentals/typography/typography';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { IGutterProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { IBorderRadiusProps, useBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';

export type CountVariant = typeof SEVERITY_COLOURS.SUCCESS | typeof SEVERITY_COLOURS.INFO | typeof SEVERITY_COLOURS.SECONDARY | typeof SEVERITY_COLOURS.ERROR;

export const countColours = ({ base, palette }: IColoursCollection) => ({
  countColours: {
    text: base.white.colour,
    outline: `1px solid ${base.white.colour}`,
    [SEVERITY_COLOURS.SUCCESS]: {
      background: palette.success
    },
    [SEVERITY_COLOURS.INFO]: {
      background: palette.info
    },
    [SEVERITY_COLOURS.SECONDARY]: {
      background: palette.neutral
    },
    [SEVERITY_COLOURS.ERROR]: {
      background: palette.error
    }
  }
});

export interface ICountProps extends IGutterProps {
  small?: boolean;
  bordered?: boolean;
  variant?: CountVariant;
  value?: number;
}

const countHeight = {
  [SIZES.SMALL]: PX_TO_REM[15], // Extreme use case for Count
  [SIZES.LARGE]: PX_TO_REM[19] // Extreme use case for Count
};

const countPadding = {
  [SIZES.SMALL]: `1px ${PX_TO_REM[4]}`,
  [SIZES.LARGE]: `${PX_TO_REM[2]} ${PX_TO_REM[6]}`
};

const Container = styled(Span)<ICountProps & IBorderRadiusProps>`
  ${useCenteredFlexCss};
  height: ${({ small }) => countHeight[small ? SIZES.SMALL : SIZES.LARGE]};
  min-width: ${({ small }) => countHeight[small ? SIZES.SMALL : SIZES.LARGE]};
  padding: ${({ small }) => countPadding[small ? SIZES.SMALL : SIZES.LARGE]};

  color: ${({ theme }) => theme.countColours.text};
  background: ${({ theme, variant }) => theme.countColours[variant!].background};

  ${useBorderRadiusCss()};

  ${({ bordered, theme }) =>
    bordered &&
    css`
      outline: ${theme.countColours.outline};
    `};

  ${useGutterAsMarginCss()};
`;

const MAX_COUNT = 99;

export const Count: React.FC<ICountProps> = ({ small, value, variant = SEVERITY_COLOURS.ERROR, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Count');

  if (value === undefined) {
    return <></>;
  }

  return (
    <Container
      borderRadius={'offgrid12'}
      noExtraLineHeight
      textSize={small ? FONT_SIZES.TEXT_NANO : FONT_SIZES.TEXT_MICRO}
      small={small}
      variant={variant}
      {...generateTestId(variant)}
      {...rest}
    >
      {value > MAX_COUNT ? `${MAX_COUNT}+` : value}
    </Container>
  );
};
